/**
 * 滚动加载更多 Hook
 * 当滚动到容器顶部时自动触发加载更多
 * 加载完成后恢复滚动位置
 */
import { useEffect, useRef, useCallback, useState } from "react";

interface UseScrollLoadMoreOptions {
    /** 是否还有更多数据 */
    hasMore: boolean;
    /** 是否正在加载 */
    loading: boolean;
    /** 触发加载的距离阈值（px），默认 5 */
    threshold?: number;
    /** 加载超时时间（ms），默认 30000（30 秒） */
    timeout?: number;
    /** 加载更多的回调函数 */
    onLoadMore: () => void;
    /** 消息列表（用于定位恢复） */
    messages: any[];
}

/**
 * 通过监听外部 callback ref 来实现滚动加载
 * 需要配合 useAutoScrollToBottom 使用
 */
export const useScrollLoadMore = (options: UseScrollLoadMoreOptions) => {
    const { hasMore, loading, threshold = 5, timeout = 30000, onLoadMore, messages } = options;

    const [container, setContainer] = useState<HTMLElement | null>(null);
    const isLoadingRef = useRef(false);
    const previousFirstMessageRef = useRef<string>("");
    const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    /**
     * 滚动事件处理
     */
    const handleScroll = useCallback(() => {
        if (!container || !hasMore || isLoadingRef.current) {
            return;
        }

        // 检查是否滚动到顶部
        const scrollTop = container.scrollTop;

        if (scrollTop <= threshold) {
            console.log("📜 滚动到顶部，触发加载更多");

            // 记录当前第一条消息的 ID（用于恢复滚动位置）
            if (messages.length > 0 && messages[0]?.message_id) {
                previousFirstMessageRef.current = messages[0].message_id;
                console.log("📝 记录第一条消息 ID:", messages[0].message_id);
            } else {
                console.warn("⚠️ 无法记录第一条消息 ID，messages:", messages);
            }

            isLoadingRef.current = true;

            // 设置超时保护：自定义时间后自动重置加载状态
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
            loadingTimeoutRef.current = setTimeout(() => {
                if (isLoadingRef.current) {
                    console.warn(`⚠️ 加载超时（${timeout}ms），自动重置加载状态`);
                    isLoadingRef.current = false;
                    previousFirstMessageRef.current = "";
                }
            }, timeout);

            onLoadMore();
        }
    }, [container, hasMore, threshold, onLoadMore, messages]);

    /**
     * 恢复滚动位置
     * 在加载完成后，将滚动条定位到之前的第一条消息
     */
    useEffect(() => {
        if (!loading && isLoadingRef.current && previousFirstMessageRef.current) {
            if (!container) {
                isLoadingRef.current = false;
                previousFirstMessageRef.current = "";
                return;
            }

            // 查找之前的第一条消息对应的 DOM 元素
            const previousFirstMessageId = previousFirstMessageRef.current;

            try {
                // 转义特殊字符，防止 querySelector 语法错误
                const escapedMessageId = CSS.escape(previousFirstMessageId);
                const messageElement = container.querySelector(`[data-message-id="${escapedMessageId}"]`);

                if (messageElement) {
                    console.log("📍 恢复滚动位置到:", previousFirstMessageId);
                    // 使用 requestAnimationFrame 确保 DOM 更新完成
                    requestAnimationFrame(() => {
                        messageElement.scrollIntoView({ block: "start" });
                    });
                } else {
                    console.warn("⚠️ 未找到之前的消息元素:", previousFirstMessageId);
                    // 降级方案：滚动到顶部偏移一点（避免完全在顶部）
                    container.scrollTop = Math.max(10, container.scrollTop);
                }
            } catch (error) {
                console.error("❌ 恢复滚动位置失败:", error);
                // 降级方案：保持当前位置
            } finally {
                // 清除超时定时器
                if (loadingTimeoutRef.current) {
                    clearTimeout(loadingTimeoutRef.current);
                    loadingTimeoutRef.current = null;
                }

                // 确保重置状态，即使出错也不影响后续加载
                isLoadingRef.current = false;
                previousFirstMessageRef.current = "";
            }
        }
    }, [container, loading, messages]);

    /**
     * 监听滚动事件
     */
    useEffect(() => {
        if (!container) return;

        container.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
            container.removeEventListener("scroll", handleScroll);
        };
    }, [container, handleScroll]);

    /**
     * 清理定时器
     */
    useEffect(() => {
        return () => {
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
        };
    }, []);

    /**
     * 返回一个用于设置容器的函数
     * 这个函数需要在外部 callback ref 被调用时同步调用
     */
    return { setContainer };
};
